import Shepherd from "shepherd.js";

/**
 * Scan Wizard Tour Configuration
 *
 * Creates an interactive tour that guides users through the scan wizard.
 * The tour adapts to the current wizard step and highlights relevant elements.
 */
export function createScanWizardTour(
  currentStep: number,
  agentType: "own" | "our-ai" | null
): InstanceType<typeof Shepherd.Tour> {
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: {
        enabled: true,
      },
      scrollTo: { behavior: "smooth", block: "center" },
      classes: "shepherd-theme-custom",
    },
    useModalOverlay: true,
  });

  // Build steps based on current wizard step
  if (currentStep === 1) {
    // Step 1: Agent Selection
    tour.addStep({
      id: "welcome",
      title: "Welcome to the Scan Wizard! 🚀",
      text: `
        <p>This wizard will guide you through setting up an AI security compliance scan.</p>
        <p class="mt-2">Let's walk through each step together!</p>
      `,
      buttons: [
        {
          text: "Skip Tour",
          action: tour.cancel,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Let's Go!",
          action: tour.next,
        },
      ],
    });

    tour.addStep({
      id: "agent-selection",
      title: "Step 1: Choose Your Agent Type",
      text: `
        <p><strong>Option 1: Quick Scan (recommended)</strong></p>
        <p class="text-sm text-muted-foreground">Use our pre-configured AI models for a quick scan. Great for quick demos!</p>
        <p class="mt-2"><strong>Option 2: Connect my agent endpoint</strong></p>
        <p class="text-sm text-muted-foreground">API + Token to test your actual application.</p>
      `,
      attachTo: {
        element: '[data-tour="connect-step"]',
        on: "bottom",
      },
      showOn() {
        return document.querySelector('[data-tour="connect-step"]') !== null;
      },
      buttons: [
        {
          text: "Back",
          action: tour.back,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Got it!",
          action: tour.complete,
        },
      ],
    });
  } else if (currentStep === 2 && agentType === "our-ai") {
    // Step 2b: Our AI Configuration
    tour.addStep({
      id: "model-selection",
      title: "Configure Your Test Model",
      text: `
        <p><strong>Select a model</strong> from the dropdown to test against.</p>
        <p class="mt-2">We support OpenAI, Anthropic, Google, and more!</p>
      `,
      attachTo: {
        element: '[data-tour="provider-cards"]',
        on: "top",
      },
      showOn() {
        return document.querySelector('[data-tour="provider-cards"]') !== null;
      },
      buttons: [
        {
          text: "Skip Tour",
          action: tour.cancel,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Next",
          action: tour.next,
        },
      ],
    });

    tour.addStep({
      id: "prompt-template",
      title: "Set Your Prompt Template",
      text: `
        <p>Enter your <strong>system prompt</strong> that defines how your AI should behave.</p>
        <p class="mt-2">Use <code>{{prompt}}</code> as a placeholder where attack prompts will be injected.</p>
        <p class="mt-2 text-sm text-muted-foreground">Tip: Paste your actual system prompt for more realistic testing!</p>
      `,
      attachTo: {
        element: "#prompt",
        on: "top",
      },
      showOn() {
        return document.querySelector("#prompt") !== null;
      },
      buttons: [
        {
          text: "Back",
          action: tour.back,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Got it!",
          action: tour.complete,
        },
      ],
    });
  } else if (currentStep === 2 && agentType === "own") {
    // Step 2a: Own Agent Configuration
    tour.addStep({
      id: "api-config",
      title: "Configure Your Agent's API",
      text: `
        <p>Enter your agent's <strong>API endpoint URL</strong> (not the chat page URL), HTTP method, headers, and request body.</p>
        <p class="mt-2">Common issues: 405 = wrong method, CORS = production endpoint, 401/403 = missing auth header.</p>
      `,
      attachTo: {
        element: '[data-tour="own-agent-config"]',
        on: "top",
      },
      showOn() {
        return document.querySelector('[data-tour="own-agent-config"]') !== null;
      },
      buttons: [
        {
          text: "Skip Tour",
          action: tour.cancel,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Got it!",
          action: tour.complete,
        },
      ],
    });
  } else if (currentStep === 3) {
    // Step 3: Plugin Selection
    tour.addStep({
      id: "compliance-frameworks",
      title: "Choose a Compliance Framework",
      text: `
        <p>Select a <strong>compliance framework</strong> like OWASP or NIST to automatically configure relevant security plugins.</p>
        <p class="mt-2">Or choose "Custom" to manually select individual plugins.</p>
      `,
      attachTo: {
        element: '[data-tour="configure-plugins-step"]',
        on: "top",
      },
      showOn() {
        return document.querySelector('[data-tour="configure-plugins-step"]') !== null;
      },
      buttons: [
        {
          text: "Skip Tour",
          action: tour.cancel,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Next",
          action: tour.next,
        },
      ],
    });

    tour.addStep({
      id: "plugins",
      title: "Security Plugins",
      text: `
        <p>Each plugin tests for specific vulnerabilities:</p>
        <ul class="mt-2 space-y-1 text-sm">
          <li>• <strong>Prompt Injection</strong> - Attempts to override instructions</li>
          <li>• <strong>PII Leakage</strong> - Tests for data exposure</li>
          <li>• <strong>Harmful Content</strong> - Checks content safety</li>
          <li>• And many more...</li>
        </ul>
      `,
      attachTo: {
        element: '[data-tour="configure-plugins-step"]',
        on: "bottom",
      },
      showOn() {
        return document.querySelector('[data-tour="configure-plugins-step"]') !== null;
      },
      buttons: [
        {
          text: "Back",
          action: tour.back,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Got it!",
          action: tour.complete,
        },
      ],
    });
  } else if (currentStep === 4) {
    // Step 4: Strategy Selection
    tour.addStep({
      id: "strategies",
      title: "Red Team Strategies",
      text: `
        <p><strong>Strategies</strong> determine how attack prompts are generated:</p>
        <ul class="mt-2 space-y-1 text-sm">
          <li>• <strong>Basic</strong> - Direct attack attempts</li>
          <li>• <strong>Jailbreak</strong> - Attempts to bypass restrictions</li>
          <li>• <strong>Multilingual</strong> - Tests in multiple languages</li>
          <li>• <strong>Crescendo</strong> - Gradual escalation attacks</li>
        </ul>
      `,
      attachTo: {
        element: '[data-tour="configure-strategies-step"]',
        on: "top",
      },
      showOn() {
        return document.querySelector('[data-tour="configure-strategies-step"]') !== null;
      },
      buttons: [
        {
          text: "Skip Tour",
          action: tour.cancel,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Next",
          action: tour.next,
        },
      ],
    });

    tour.addStep({
      id: "run-scan",
      title: "Ready to Run! 🎯",
      text: `
        <p>Click <strong>"Proceed"</strong> to start your security scan.</p>
        <p class="mt-2">The scan will run in the background and you'll be notified when results are ready.</p>
        <p class="mt-2 text-sm text-muted-foreground">Scans typically take 5-15 minutes depending on configuration.</p>
      `,
      attachTo: {
        element: '[data-tour="run-scan-button"]',
        on: "top",
      },
      showOn() {
        return document.querySelector('[data-tour="run-scan-button"]') !== null;
      },
      buttons: [
        {
          text: "Back",
          action: tour.back,
          classes: "shepherd-button-secondary",
        },
        {
          text: "Got it!",
          action: tour.complete,
        },
      ],
    });
  }

  return tour;
}
